import {
  addDoc,
  collection,
  doc,
  increment,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getOrCreateAnonymousSessionId, normalizeSearchQuery } from './searchAnalyticsService';

const EVENTS_COLLECTION = 'analytics_events';
const DAILY_COLLECTION = 'analytics_daily';
const DEDUPE_KEY = 'kawescar_analytics_last_event_v1';
const DEDUPE_WINDOW_MS = 3000;

const CONTACT_TYPES = new Set(['contact_click', 'whatsapp_click', 'phone_click', 'email_click']);

function chileNowParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const pick = (type) => parts.find((p) => p.type === type)?.value || '';
  const year = pick('year');
  const month = pick('month');
  const day = pick('day');
  const hour = pick('hour');
  const dateKey = `${year}-${month}-${day}`;
  const hourKey = `${dateKey}-${hour}`;
  return { dateKey, hourKey };
}

function buildDedupeSignature(payload) {
  const empresaId = payload.empresaId || '';
  const tipId = payload.tipId || '';
  const searchText = normalizeSearchQuery(payload.searchText || '');
  return [payload.type || '', payload.page || '', empresaId, tipId, searchText, payload.sessionId || ''].join('|');
}

function shouldSkipByDedupe(payload) {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const signature = buildDedupeSignature(payload);
    const now = Date.now();
    const raw = sessionStorage.getItem(DEDUPE_KEY);
    const prev = raw ? JSON.parse(raw) : null;
    if (prev?.signature === signature && now - Number(prev?.ts || 0) < DEDUPE_WINDOW_MS) {
      return true;
    }
    sessionStorage.setItem(DEDUPE_KEY, JSON.stringify({ signature, ts: now }));
    return false;
  } catch {
    return false;
  }
}

function dailyFieldByType(type) {
  switch (type) {
    case 'home_visit':
      return 'homeVisits';
    case 'search':
      return 'searches';
    case 'profile_visit':
      return 'profileVisits';
    case 'tip_view':
      return 'tipVisits';
    default:
      return CONTACT_TYPES.has(type) ? 'contactClicks' : null;
  }
}

function isContactEvent(type) {
  return CONTACT_TYPES.has(type);
}

export async function logAnalyticsEvent({
  type,
  empresaId = null,
  empresaNombre = null,
  tipId = null,
  tipSlug = null,
  tipTitulo = null,
  page = '/',
  searchText = null,
  userId = null,
  source = 'web',
  dedupe = true,
}) {
  if (!db || !type) return;
  const sessionId = getOrCreateAnonymousSessionId() || `anon_${Date.now()}`;
  const uid = auth?.currentUser?.uid || userId || null;
  const { dateKey, hourKey } = chileNowParts();

  const payload = {
    type,
    empresaId: empresaId || null,
    empresaNombre: empresaNombre || null,
    tipId: tipId ? String(tipId).trim() : null,
    tipSlug: tipSlug ? String(tipSlug).trim() : null,
    tipTitulo: tipTitulo ? String(tipTitulo).trim() : null,
    page: page || '/',
    searchText: searchText ? String(searchText).trim() : null,
    userId: uid,
    sessionId,
    source,
    createdAt: serverTimestamp(),
    dateKey,
    hourKey,
  };

  if (dedupe && shouldSkipByDedupe(payload)) return;

  try {
    await addDoc(collection(db, EVENTS_COLLECTION), payload);
  } catch (err) {
    console.warn('⚠️ analytics_events addDoc error:', err?.message || err);
  }

  const dailyField = dailyFieldByType(type);
  if (!dailyField) return;

  try {
    const dailyRef = doc(db, DAILY_COLLECTION, dateKey);
    await setDoc(
      dailyRef,
      {
        dateKey,
        [dailyField]: increment(1),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    if (empresaId) {
      const companyRef = doc(db, DAILY_COLLECTION, dateKey, 'empresas', empresaId);
      const companyData = {
        empresaId,
        empresaNombre: empresaNombre || null,
        updatedAt: serverTimestamp(),
      };
      if (type === 'profile_visit') {
        companyData.profileVisits = increment(1);
        companyData.lastVisitAt = serverTimestamp();
      }
      if (isContactEvent(type)) {
        companyData.contactClicks = increment(1);
      }
      await setDoc(companyRef, companyData, { merge: true });
    }

    const tipDocId = tipId ? String(tipId).trim() : '';
    if (tipDocId && type === 'tip_view') {
      const tipRef = doc(db, DAILY_COLLECTION, dateKey, 'tips', tipDocId);
      await setDoc(
        tipRef,
        {
          tipId: tipDocId,
          tipSlug: tipSlug ? String(tipSlug).trim() : null,
          tipTitulo: tipTitulo ? String(tipTitulo).trim() : null,
          visits: increment(1),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  } catch (err) {
    console.warn('⚠️ analytics_daily upsert error:', err?.message || err);
  }
}

export function getDateKeyChile() {
  return chileNowParts().dateKey;
}

